import React, { useState, useMemo } from 'react';
import {
  ArrowLeft,
  ArrowUpRight,
  Filter,
  MoreVertical,
  Search,
} from 'lucide-react';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '../components/ui/button.tsx';
import { Progress } from '../components/ui/progress.tsx';
import { Card } from '../components/ui/card.tsx';
import { Input } from '../components/ui/input.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown.tsx';

import {
  useGetAllSessions,
  useUpdateSession,
  useDeleteSession,
} from '../api/session/sessionQueries';
import { AddSessionModal } from '../components/sessions/AddSessionModal';
import { SessionEditPanel } from '../components/sessions/SessionEditPanel';
import { CreateTermModal } from '../components/sessions/CreateTermModal.jsx';
import TermCard from '../components/sessions/TermCard.jsx';

export default function SessionsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [isAddSessionOpen, setIsAddSessionOpen] = useState(false);
  const [isCreateTermOpen, setIsCreateTermOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);

  const { data: sessionsData = [], isLoading } = useGetAllSessions();

  const sessions = useMemo(() => {
    if (!sessionsData?.data) return [];
    return Array.isArray(sessionsData.data) ? sessionsData.data : [];
  }, [sessionsData]);

  const { mutate: updateSession } = useUpdateSession();
  const { mutate: deleteSession } = useDeleteSession();

  // Calculate stats from session data
  const stats = useMemo(() => {
    if (sessions.length === 0)
      return {
        totalStudents: 0,
        totalStaff: 0,
        totalRevenue: 0,
        studentGrowth: 0,
        staffGrowth: 0,
      };

    return sessions.reduce(
      (acc, session) => {
        // Sum up the stats from each session
        acc.totalStudents += session.enrollments?.length || 0;
        acc.totalStaff += session.staff?.length || 0;
        acc.totalRevenue += session.revenue || 0;

        // Calculate growth percentages
        if (sessions.length > 1) {
          const prevSession = sessions[sessions.length - 2]; // Get previous session
          if (prevSession) {
            const prevStudents = prevSession.enrollments?.length || 0;
            const prevStaff = prevSession.staff?.length || 0;
            acc.studentGrowth = prevStudents
              ? ((acc.totalStudents - prevStudents) / prevStudents) * 100
              : 0;
            acc.staffGrowth = prevStaff
              ? ((acc.totalStaff - prevStaff) / prevStaff) * 100
              : 0;
          }
        }

        return acc;
      },
      {
        totalStudents: 0,
        totalStaff: 0,
        totalRevenue: 0,
        studentGrowth: 0,
        staffGrowth: 0,
      }
    );
  }, [sessions]);

  const currentSession = useMemo(() => {
    if (sessions.length === 0) return null;
    return sessions.reduce((latest, current) => {
      if (!latest) return current;
      return new Date(current.startDate) > new Date(latest.startDate)
        ? current
        : latest;
    }, null);
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    if (sessions.length === 0) return [];

    let filtered = [...sessions];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (session) =>
          session?.sessionId?.toString()?.includes(query) ||
          session?.name?.toLowerCase()?.includes(query) ||
          session?.startDate?.toLowerCase()?.includes(query) ||
          session?.endDate?.toLowerCase()?.includes(query)
      );
    }

    filtered.sort((a, b) => {
      if (!a || !b) return 0;

      if (sortOrder === 'newest') {
        return new Date(b.startDate) - new Date(a.startDate);
      } else if (sortOrder === 'oldest') {
        return new Date(a.startDate) - new Date(b.startDate);
      }
      return 0;
    });

    return filtered;
  }, [sessions, searchQuery, sortOrder]);

  const handleEditSession = (session) => {
    setSelectedSession(session);
    setIsEditPanelOpen(true);
  };

  const handleEditTerm = (term) => {
    setSelectedTerm(term);
    setIsCreateTermOpen(true);
  };

  const handleUpdateSession = async (sessionId, data) => {
    try {
      await updateSession({ sessionId, data });
      toast.success('Session updated successfully');
      setIsEditPanelOpen(false);
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session?')) {
      return;
    }

    try {
      await deleteSession(sessionId);
      toast.success('Session deleted successfully');
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="rounded-2xl border bg-white p-4 mb-6">
        <div className="mb-4">
          <div className="flex items-center mb-3">
            <span className="border-r border-gray-300 mr-4">
              <h3 className="mr-3 text-base font-medium">
                {currentSession ? currentSession.name : 'No Active Session'}
              </h3>
            </span>
            <span className="flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">
              <span
                className={`mr-1 h-1.5 w-1.5 rounded-full ${
                  currentSession ? 'bg-green-500' : 'bg-gray-500'
                }`}
              ></span>
              {currentSession ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="flex flex-col md:flex-row gap-4 ">
            <Card className="bg-gray-100 p-4 w-full md:w-44 rounded-2xl">
              <div className="text-sm text-gray-600">Enrolled Students</div>
              <div className="flex items-center mt-1.5">
                <div className="text-2xl font-semibold">
                  {stats.totalStudents.toLocaleString()}
                </div>
                <div className="ml-2 flex items-center text-xs text-green-600">
                  <span>
                    {stats.studentGrowth > 0 ? '+' : ''}
                    {stats.studentGrowth.toFixed(2)}%
                  </span>
                  <ArrowUpRight className="h-3 w-3" />
                </div>
              </div>
            </Card>

            <Card className="bg-gray-100 p-4 w-full md:w-44 rounded-2xl">
              <div className="text-sm text-gray-600">Active Staff</div>
              <div className="flex items-center mt-1.5">
                <div className="text-2xl font-semibold">
                  {stats.totalStaff.toLocaleString()}
                </div>
                <div className="ml-2 flex items-center text-xs text-green-600">
                  <span>
                    {stats.staffGrowth > 0 ? '+' : ''}
                    {stats.staffGrowth.toFixed(2)}%
                  </span>
                  <ArrowUpRight className="h-3 w-3" />
                </div>
              </div>
            </Card>
            <Card className="bg-purple-600 p-4 text-white w-full md:w-56 rounded-2xl">
              <div className="text-sm">Total Revenue</div>
              <div className="text-2xl font-semibold mt-1.5">
                N{' '}
                {stats.totalRevenue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </Card>
          </div>

          <div className="border-l-2 border-gray-400 w-1/5 flex flex-col pl-3">
            <div className="flex flex-col">
              {currentSession.terms.length === 0 ? (
                <p>No terms available</p>
              ) : (
                <p>
                  {currentSession.terms.map((term) => (
                    <TermCard
                      key={term.id}
                      term={term}
                      handleEditTerm={handleEditTerm}
                    />
                  ))}
                </p>
              )}
            </div>
            <Button
              className="self-end bg-white hover:bg-purple-700 hover:text-white text-purple-700 border border-purple-700"
              onClick={() => setIsCreateTermOpen(true)}
            >
              Create New Term
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filter
            </Button>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                className="pl-8"
                placeholder="Search Session by Date, ID"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <Button
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => setIsAddSessionOpen(true)}
          >
            + New Session
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead>
              <tr className="border-b text-left text-sm text-gray-500">
                <th className="pb-2 pt-4 font-medium">SESSION ID</th>
                <th className="pb-2 pt-4 font-medium">NO OF STUDENT</th>
                <th className="pb-2 pt-4 font-medium">NO OF EMPLOYEES</th>
                <th className="pb-2 pt-4 font-medium">START DATE</th>
                <th className="pb-2 pt-4 font-medium">END DATE</th>
                <th className="pb-2 pt-4 font-medium"></th>
                <th className="pb-2 pt-4 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-4 text-center">
                    Loading sessions...
                  </td>
                </tr>
              ) : filteredSessions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-4 text-center">
                    No sessions found
                  </td>
                </tr>
              ) : (
                filteredSessions.map((session) => (
                  <tr key={session.sessionId} className="border-b">
                    <td className="py-4 text-purple-600">
                      {session.sessionId}
                    </td>
                    <td className="py-4">
                      {(session.enrollments?.length || 0).toLocaleString()}
                    </td>
                    <td className="py-4">
                      {(session.staff?.length || 0).toLocaleString()}
                    </td>
                    <td className="py-4">
                      {format(new Date(session.startDate), 'dd/MMM/yyyy')}
                    </td>
                    <td className="py-4">
                      {format(new Date(session.endDate), 'dd/MMM/yyyy')}
                    </td>
                    <td className="py-4 text-purple-600">
                      <Button
                        variant="link"
                        className="text-purple-600 hover:text-purple-800"
                        onClick={() => navigate('/departments')}
                      >
                        View Details
                      </Button>
                    </td>
                    <td className="py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleEditSession(session)}
                          >
                            Edit Session
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() =>
                              handleDeleteSession(session.sessionId)
                            }
                          >
                            Delete Session
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-sm text-gray-500">Rows per page: </span>
            <select className="ml-2 rounded border-none bg-transparent text-sm">
              <option>10</option>
              <option>20</option>
              <option>50</option>
            </select>
          </div>
          <div className="flex items-center">
            <span className="mr-4 text-sm text-gray-500">
              {`1-${filteredSessions.length} of ${sessions.length}`}
            </span>
            <div className="flex">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4 rotate-180" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AddSessionModal
        open={isAddSessionOpen}
        onOpenChange={setIsAddSessionOpen}
      />

      <CreateTermModal
        open={isCreateTermOpen}
        onOpenChange={setIsCreateTermOpen}
        sessions={sessions}
        term={selectedTerm}
        setTerm={setSelectedTerm}
      />

      <SessionEditPanel
        session={selectedSession}
        isOpen={isEditPanelOpen}
        onClose={() => {
          setIsEditPanelOpen(false);
          setSelectedSession(null);
        }}
        onUpdate={handleUpdateSession}
      />
    </div>
  );
}
